import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box, CircularProgress, Fab, Typography, Button } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import type Hls from "hls.js";
import { useVideoEngine } from "./useVideoEngine";
import { usePlayerHotkeys } from "./usePlayerHotkeys";
import { useIdle } from "./useIdle";
import { PlayerFooter } from "./PlayerFooter";
import { VideoAnnotationsOverlay } from "./VideoAnnotationsOverlay";
import type { PlayerTimecode } from "./playerTimecode";
import { useTranslation } from "react-i18next";

export interface PlayerProps {
  src?: string | null;
  poster?: string | null;
  /** Content title shown (with a short delay) in the pause overlay. */
  title?: string | null;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  hotkeys?: boolean;
  startPosition?: number | null;
  fill?: boolean;
  overlay?: ReactNode;
  pauseOverlay?: ReactNode;
  onTimeUpdate?: (video: HTMLVideoElement) => void;
  onPause?: (video: HTMLVideoElement) => void;
  onEnded?: (video: HTMLVideoElement) => void;
  onEngineError?: () => void;
  /** Метки таймлайна: MARKER — иконки на сикбаре (+тумблер в футере),
   * ANNOTATION — всплывающие карточки поверх видео. */
  timecodes?: PlayerTimecode[] | null;
  getVideoElement?: (video: HTMLVideoElement | null) => void;
  /** Инстанс hls.js — нужен потребляителям, которым мало HTMLVideoElement
   * (например, hls.playingDate для позиции в живом эфире). Зеркалит
   * getVideoElement. */
  getHlsInstance?: (hls: Hls | null) => void;
  /** Called on any user gesture that expresses intent to start playback
   * (clicking the video area or the big play button), even before `src` is
   * available. Lets the caller gate session/view creation on this instead of
   * firing it unconditionally on mount regardless of `autoPlay`. */
  onPlayIntent?: () => void;
}

export const Player: FC<PlayerProps> = ({
  src,
  poster,
  title,
  autoPlay = false,
  muted = false,
  loop = false,
  controls = true,
  hotkeys = true,
  startPosition,
  fill = false,
  overlay,
  pauseOverlay,
  onTimeUpdate,
  onPause,
  onEnded,
  onEngineError,
  timecodes,
  getVideoElement,
  getHlsInstance,
  onPlayIntent,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const { videoRef, hlsRef, state, actions } = useVideoEngine({
    src,
    autoPlay,
    muted,
    loop,
    startPosition,
  });
  const { idle, wake } = useIdle(5000);
  const controlsVisible =
    controls && (!idle || state.paused || state.isLoading);

  // Start (or restart) the auto-hide countdown as soon as playback starts, so
  // controls hide after 5s even without any prior mouse/touch activity.
  useEffect(() => {
    if (!state.paused) wake();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.paused]);

  // The whole pause overlay (backdrop + Fab + title) fades in together, 2s
  // after pausing — matches reference PlayerPause, which gates backdrop
  // opacity, inner opacity and title on the single `visible` flag set by one
  // SHOW_TIMEOUT(2000) timer, instead of popping in immediately.
  const [pauseOverlayVisible, setPauseOverlayVisible] = useState(false);
  useEffect(() => {
    if (!state.paused) {
      setPauseOverlayVisible(false);
      return undefined;
    }
    const timer = setTimeout(() => setPauseOverlayVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [state.paused]);

  useEffect(() => {
    getVideoElement?.(videoRef.current);
    return () => getVideoElement?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  useEffect(() => {
    getHlsInstance?.(hlsRef.current);
    return () => getHlsInstance?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const handleTime = () => onTimeUpdate?.(video);
    const handlePause = () => onPause?.(video);
    const handleEnded = () => onEnded?.(video);
    video.addEventListener("timeupdate", handleTime);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    return () => {
      video.removeEventListener("timeupdate", handleTime);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, onTimeUpdate, onPause, onEnded]);

  useEffect(() => {
    if (state.error) onEngineError?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.error]);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else if (container.requestFullscreen) {
      void container.requestFullscreen();
    } else {
      // iOS Safari fallback
      const video = container.querySelector("video") as HTMLVideoElement & {
        webkitEnterFullscreen?: () => void;
      };
      video?.webkitEnterFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const togglePip = useCallback(async () => {
    const video = containerRef.current?.querySelector("video");
    if (!video) return;
    try {
      if (document.pictureInPictureElement)
        await document.exitPictureInPicture();
      else await video.requestPictureInPicture();
    } catch {
      // PiP unsupported
    }
  }, []);

  usePlayerHotkeys(state, actions, {
    enabled: hotkeys,
    onToggleFullscreen: toggleFullscreen,
  });

  return (
    <Box
      ref={containerRef}
      onMouseMove={wake}
      onTouchStart={wake}
      sx={{
        position: "relative",
        width: "100%",
        ...(fill ? { height: "100%" } : { aspectRatio: "16 / 9" }),
        backgroundColor: "#000",
        overflow: "hidden",
        cursor: controlsVisible ? "default" : "none",
      }}
    >
      <video
        ref={videoRef}
        playsInline
        poster={poster ?? undefined}
        style={{ width: "100%", height: "100%" }}
        onClick={() => {
          onPlayIntent?.();
          actions.togglePlay();
        }}
        onDoubleClick={toggleFullscreen}
      />
      {state.isLoading && !state.error && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      )}
      {controls && state.paused && !state.isLoading && !state.error && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: pauseOverlayVisible
              ? "rgba(0,0,0,0.6)"
              : "rgba(0,0,0,0)",
            transition: "background-color 0.3s ease",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
              opacity: pauseOverlayVisible ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          >
            <Fab
              color="primary"
              onClick={() => {
                onPlayIntent?.();
                actions.togglePlay();
              }}
              sx={{ pointerEvents: "auto", width: 72, height: 72 }}
              aria-label={t("shared.player.play", { defaultValue: "Смотреть" })}
            >
              <PlayArrowIcon sx={{ fontSize: 48 }} />
            </Fab>
            {/* Skip the built-in title when the caller supplies its own
                pauseOverlay (e.g. embed's clickable title-link) to avoid
                rendering the title twice. */}
            {title && !pauseOverlay && (
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  textAlign: "center",
                  px: 2,
                  fontSize: { xs: "0.7rem", sm: "1.3rem", md: "1.6rem" },
                }}
              >
                {title}
              </Typography>
            )}
            {pauseOverlay}
          </Box>
        </Box>
      )}
      {state.error && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
          }}
        >
          <Typography variant="body2">
            {t("shared.player.error", {
              defaultValue: "Не удалось воспроизвести видео",
            })}
          </Typography>
          <Button
            startIcon={<ReplayIcon />}
            onClick={actions.retry}
            sx={{ color: "#fff" }}
          >
            {t("shared.player.retry", { defaultValue: "Повторить" })}
          </Button>
        </Box>
      )}
      <VideoAnnotationsOverlay
        timecodes={timecodes}
        currentTime={state.currentTime}
        paused={state.paused}
        resetKey={src}
      />
      {overlay}
      {controls && (
        <PlayerFooter
          src={src}
          state={state}
          actions={actions}
          visible={controlsVisible}
          fullscreen={fullscreen}
          onToggleFullscreen={toggleFullscreen}
          onTogglePip={togglePip}
          timecodes={timecodes}
        />
      )}
    </Box>
  );
};
